import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, skipWhile } from 'rxjs/operators';
import { Config } from '../models/config.model';
import { FileStatus, UploadFile } from '../models/uploadfile.model';
import { ConfigService } from './config.service';
import { SnackbarMessageComponent } from '../components/snackbar/snackbar-message/snackbar-message.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthenticationService } from './authentication.service';

@Injectable({
    providedIn: 'root'
})
export class UploadService {

    private _isUploading!: Subject<boolean>;
    public isUploading!: Observable<boolean>;

    private maxUploadFileSize: number = 0;
    private allowedContentTypes: string[] = [];
    private uploadedFiles: UploadFile[] = [];
    private selectedIndexes: number[] = [];


    get files(): UploadFile[] {
        return this.uploadedFiles;
    }

    constructor(
        private authenticationService: AuthenticationService,
        private configService: ConfigService, 
        private snackbar: MatSnackBar, 
        private httpClient: HttpClient) {
        
        this._isUploading = new Subject<boolean>();
        this.isUploading = this._isUploading.asObservable();

        this.configService.config
            .pipe(skipWhile(v => v === undefined || v === null))
            .subscribe(
                (data: Config | null) => {
                    if (data != null) {
                        let config = data;

                        this.maxUploadFileSize = config.MaxUploadFileSize;
                        let allowedContentTypes = config.ContentTypes;

                        allowedContentTypes.forEach(element => {
                            this.allowedContentTypes.push(element);
                        });

                    }
                }
            );
    }

    public formatBytes(bytes: number | undefined, decimals = 2) {
        if (bytes === undefined || bytes === 0) {
            return "0 Bytes";
        }
        const k = 1024;
        const dm = decimals <= 0 ? 0 : decimals;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    }

    public downloadFile(file: UploadFile) {

        this.httpClient.get('/api/document/' + file.deleteKey?.Key, { observe: 'response', responseType: 'arraybuffer' })
        .subscribe(
            (response: any) => {
                this.openFile(response.body, file.name, file.type);
            },
            (error: HttpErrorResponse) => {
                console.log(error);
            },
            () => {
                console.log('Finished open file');
            }
        );
    }

    public removeFile(file: UploadFile, notify: boolean) {

        let selectedFileIndex = this.uploadedFiles.findIndex(x => x.id === file.id);
        if (selectedFileIndex > -1) {
            this.uploadedFiles.splice(selectedFileIndex, 1);

            if (file.deleteKey?.Key) {
                this.httpClient.delete('/api/document/deletefile/' + file.deleteKey.Key)
                .subscribe(
                    (response) => {
                        if (notify) {
                            this.snackbar.openFromComponent(SnackbarMessageComponent, {data: {message:'Fjernet filen ' + file.name, type: 'info'}});
                        }
                    },
                    (error: HttpErrorResponse) => {
                        console.log(error);
                    },
                    () => {
                        console.log('Finished remove file');
                    }
                );
            }
        }
    }

    public addFiles(files: FileList) {
        
        this._isUploading.next(true);
        
        for (let i = 0; i < files.length; i++) {
            let file: File = files[i];
            
            if (this.fileAllowed(file)) {
                let uf: UploadFile = {
                    id: this.generateId(),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    progress: 0,
                    status: FileStatus.Reading,
                    nativeFile: file,
                }        
                this.uploadedFiles.push(uf);
                this.selectedIndexes.push(this.uploadedFiles.findIndex(f => f.id === uf.id));
            }
        }

        this.loadNextFile();
    }

    public clearFiles(hardDelete: boolean, notify: boolean) {

        if (hardDelete) {
            this.uploadedFiles.forEach(
                (file: UploadFile) => {
                    this.removeFile(file, notify);
                }
            );    
        }

        this.uploadedFiles.length = 0;
    }

    private loadNextFile(): void {

        if (this.selectedIndexes.length <= 0) {
            //last file finished, emit event finished uploading
            this._isUploading.next(false);
            return;
        }

        let index: number | undefined = this.selectedIndexes.shift();

        console.log(`'Load file with index ${index}'`);

        if (index !== undefined && index > -1) {
            let file: UploadFile = this.uploadedFiles[index];
            this.readFileContents(file);
        } else {
            //last file finished, emit event finished uploading
            this._isUploading.next(false);
        }

    }

    private readFileContents(file: UploadFile) {

        let reader = new FileReader()

        reader.onabort = (event: ProgressEvent<FileReader>) => {
            console.log(`'${file.name}' aborted`);
        }

        reader.onloadstart = (event: ProgressEvent<FileReader>) => {
            console.log(`'${file.name}' loadstart: ${event.loaded} of ${event.total} bytes loaded. Progress can be measured: ${event.lengthComputable}`);
        }

        reader.onprogress = (event: ProgressEvent<FileReader>) => {
            const completed = Math.round((event.loaded / event.total) * 100);
            console.log(`'${file.name}' progress: ${event.loaded} of ${event.total} bytes loaded. (${completed}% completed)`);
            file.progress = completed;
        }

        reader.onerror = (event: ProgressEvent<FileReader>) => {

            let error: DOMException | null = reader.error;

            if (error !== null) {
                console.log(`'${file.name}' error: ${error.name} - ${error.message}`);
                this.snackbar.openFromComponent(SnackbarMessageComponent, {data: {message:'Det oppsto en feil ved innlesing av filen\n' + error.message, type: 'error'}});
                this.removeFile(file, false);
                
                //todo: fikse emit her også hvis den feiler
                this.loadNextFile(); //continue if this file fails
            }
        }

        reader.onload = (event: ProgressEvent<FileReader>) => {
            console.log(`'${file.name}' load: ${event.loaded} of ${event.total} bytes loaded. Progress can be measured: ${event.lengthComputable}`);
        }
        
        reader.onloadend = (event: ProgressEvent<FileReader>) => {
            console.log(`'${file.name}' loadend: ${event.loaded} of ${event.total} bytes loaded. Progress can be measured: ${event.lengthComputable}`);

            if (reader.readyState === FileReader.DONE) {
                if (reader.result !== null) {
                    file.fileResult = new Blob([reader.result]);
                    this.upload(file);
                }
            }
        }

        reader.readAsArrayBuffer(file.nativeFile)
    }

    private upload(file: UploadFile) {

        if (file.fileResult) {
            const formData = new FormData();
            formData.append('file', file.fileResult, file.name);
            file.status = FileStatus.Uploading;
            file.progress = 0;

            let headers: HttpHeaders = new HttpHeaders();
            headers.set('Content-Type', file.type)

            this.postFile(formData, headers)
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return of(error); //of(`${file.nativeFile.name} upload failed.`);
                    }))
                .subscribe(
                    (response: HttpErrorResponse | HttpEvent<any>) => {
                        console.log(response);

                        if (response instanceof HttpErrorResponse) {
                            file.status = FileStatus.Failed;
                            this.removeFile(file, false);


                            this.loadNextFile();
                        }
                        else {
                            if (response.type === HttpEventType.Response) {
                                file.status = FileStatus.Done;
                                file.deleteKey = response.body;                                          
                                this.loadNextFile(); //continue next file
                            }
                            else if (response.type === HttpEventType.UploadProgress) {
                                if (response.total) {
                                    let progress = Math.round(response.loaded * 100 / response.total);
                                    file.progress = progress;
                                }
                            }
                        } 
                    });
        }

    }

    private postFile(formData: FormData, headers: HttpHeaders) {

        let url: string = '/api/document/uploadfile';
        return this.httpClient.post<any>(url, formData, {
            reportProgress: true,
            observe: 'events',
            headers: headers
        });
    }

    private fileAllowed(file: File) {

        // if (this.fileExists(file)) {
        //     this.snackbar.open('Filen ' + file.name + ' er allerede lagt til', 'Advarsel');
        //     return false;
        // }

        if (this.allowedContentTypes.findIndex(x => x === file.type) < 0) {
            this.snackbar.openFromComponent(SnackbarMessageComponent, {data: {message:'Filen ' + file.name + ' er ikke en gyldig filtype', type: 'warning'}});
            return false;
        }

        if (file.size > this.maxUploadFileSize) {
            this.snackbar.openFromComponent(SnackbarMessageComponent, {data: {message:'Filen ' + file.name + ' er for stor', type: 'warning'}});
            return false;
        }

        return true;
    }
    
    private fileExists(file: any) {
        let exists = this.uploadedFiles.findIndex(x => x.name.toLowerCase() === file.name.toLowerCase()) > -1;
        return exists;
    }
    
    private generateId() {
        return Math.random().toString(36).substring(7);
    }
    
    private openFile(response: ArrayBuffer, fileName: string, contentType: string) {
        const blob = new Blob([response], { type: contentType });

        //if (contentType.toLowerCase().startsWith('application/pdf') || contentType.toLowerCase().startsWith('image') || contentType.toLowerCase().startsWith('text')) {
            // new tab
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        //} else {
        //    this.fileSaverService.save(blob, fileName);
        //}

    }

}